import React, { useRef, useState } from "react";
import { format } from "date-fns";
import { toZonedTime, fromZonedTime } from "date-fns-tz";
import { toast } from "react-toastify";
import { useOutsideClickDetection } from "../../../../lib/hooks/useOutsideClickDetection";
import { useRouter } from "../../../../lib/hooks/useRouter";
import { useToggleModal } from "../../../../lib/hooks/useToggleModal";
import { useTimeZoneContext } from "../../../../lib/context/TimeZone/TimeZoneContext";
import { getReservationTypes } from "../helpers/getReservationActionsTypes";
import { useCheckUserPermissions } from "../../../../lib/hooks/useCheckUserPermissions";
import { useBookingsMapContext } from "../../BookingsMap/Context/BookingsMapContext";
import { attachHoursToDate } from "../../lib/dateInputConvert";

import {
  Event as RoomEvent,
  EventCheckInStatus,
} from "../../../../api/grpc/booking/ggevent/ggevent";
import { DeskLocationItem } from "../../../../api/grpc/desk/desk";
import { Event as DeskEvent } from "../../../../api/grpc/desk/ggevent/ggevent";
import { RESERVATIONS_MAP_ROOT_PATH } from "../../../../lib/routes/routes";
import {
  DropdownList,
  DropdownListOptions,
} from "../../../shared/DropdownList/DropdownList";
import { Icon } from "../../../shared/Icon/Icon";
import { CheckInDeskModal } from "../../Reserve/Modals/CheckInDeskModal";
import { CheckOutModal } from "../../Reserve/Modals/CheckOutModal";
import { DeleteModal } from "../../Reserve/Modals/DeleteModal";
import { UpdateReservationModal } from "../../Reserve/Modals/UpdateReservationModal";
import { CheckInRequired } from "../../Reserve/Modals/CheckInRequired";
import { MainResources } from "../../../../lib/types/main.types";
import { GoToMyReservationModal } from "../../Reserve/Modals/GoToMyReservationModal";
import { UserPermissions } from "../../../../api/grpc/permissions/permissions";

type ModalTypes =
  | "modifyReservation"
  | "deleteReservation"
  | "checkInDesk"
  | "checkOut";

export interface Props {
  type: MainResources;
  workplaceId: string;
  event: DeskEvent | RoomEvent;
  startTime: string;
  endTime: string;
  name: string;
  locationPath: DeskLocationItem[];
  checkInStatus: EventCheckInStatus | null;
  allowDeleteOption: boolean;
  isAttendee?: boolean;
  qrCodeRequired?: boolean;
  refetchDesk?: () => void;
  refetchRoom?: () => void;
  isAuthedUsersEvent?: boolean;
  allowToModifyEvent?: boolean;
  disableEventActions?: boolean;
  checkInTime?: number;
  organizerName?: string;
  hideMeetingTitle?: boolean;
  mode2Organizer?: boolean;
  showAttendees?: boolean;
}

export interface ExtendedProps extends Props {
  isOpen: boolean;
  toggleModal: () => void;
  userName: string;
}

export const ReservationListActions = ({
  type,
  workplaceId,
  event,
  refetchDesk,
  refetchRoom,
  startTime,
  endTime,
  name,
  locationPath,
  checkInStatus,
  qrCodeRequired,
  allowDeleteOption,
  isAttendee,
  isAuthedUsersEvent,
  checkInTime,
  organizerName,
  hideMeetingTitle,
  mode2Organizer,
  showAttendees,
}: Props) => {
  const ref = useRef(null);
  const { checkUserPermission } = useCheckUserPermissions();
  const { history, pathname } = useRouter();
  const { isOpen, toggleModal } = useToggleModal();
  const { timeZone } = useTimeZoneContext();
  const { handleDay, handleTime, setDateAndTime } = useBookingsMapContext();

  const [showDropdown, setShowDropdown] = useState(false);
  const [activeModal, setActiveModal] = useState<ModalTypes | undefined>(
    undefined
  );
  const [hide, setHide] = useState("d-none");

  const memberView = checkUserPermission(
    UserPermissions.RSRVNS_MEMBER_STYLE_VIEW
  );

  const canModifyEvent = isAuthedUsersEvent || !memberView;

  const disableEventActions = !canModifyEvent || isAttendee;

  const components: {
    [key in ModalTypes]: React.FunctionComponent<any>;
  } = {
    modifyReservation: mode2Organizer
      ? GoToMyReservationModal
      : UpdateReservationModal,
    deleteReservation: mode2Organizer ? GoToMyReservationModal : DeleteModal,
    checkInDesk:
      qrCodeRequired && memberView ? CheckInRequired : CheckInDeskModal,
    checkOut: mode2Organizer ? GoToMyReservationModal : CheckOutModal,
  };

  const options: DropdownListOptions[] = getReservationTypes({
    typeOfCheckin: checkInStatus,
    workplaceType: type,
    startTime: event?.startTime,
    isCheckInRequired: event.checkInRequiredStatus,
    allowDeleteOption: allowDeleteOption,
    allDay: event.allDay,
    status: event?.locations[0]?.status,
    isAttendee:
      pathname?.includes("all-reservations") && !memberView
        ? false
        : isAttendee,
    allowToModifyEvent: canModifyEvent,
  });

  const Component = components[activeModal as ModalTypes];

  let componentProps: ExtendedProps = {
    workplaceId: workplaceId,
    name: name,
    userName: organizerName || "",
    refetchDesk: refetchDesk,
    refetchRoom: refetchRoom,
    locationPath: locationPath,
    isOpen: isOpen,
    toggleModal: toggleModal,
    startTime: startTime,
    endTime: endTime,
    event: event,
    type: type,
    checkInStatus: checkInStatus,
    allowDeleteOption: allowDeleteOption,
    isAttendee: isAttendee,
    checkInTime: checkInTime,
    allowToModifyEvent: canModifyEvent,
    disableEventActions: disableEventActions,
    hideMeetingTitle: hideMeetingTitle,
    showAttendees: showAttendees || isAuthedUsersEvent,
    isAuthedUsersEvent: isAuthedUsersEvent,
  };

  useOutsideClickDetection(ref, () => {
    setHide("d-none");
    setShowDropdown(false);
  });

  return (
    <div ref={ref}>
      <Icon
        icon="actions-icon"
        className={hide}
        onClick={() => {
          if (showDropdown) {
            setHide("d-none");
          }

          setHide("d-block");
          setShowDropdown((prev) => !prev);
        }}
      />

      {showDropdown && (
        <DropdownList
          extended
          options={options}
          color="gray"
          onClick={(value) => {
            if (value === "openMap") {
              let clickedMarker = locationPath.filter(
                (location) => location.type === "Floor"
              );

              if (clickedMarker.length <= 0) {
                return toast.warn("This workspace is not part of a floor!");
              }

              // set time of floor map to be the event's start time
              const eventStart = toZonedTime(new Date(startTime), timeZone);
              const eventDay = format(eventStart, "yyyy-MM-dd");
              const zonedDay = fromZonedTime(eventDay, timeZone);
              const normalizedDayIso = toZonedTime(
                zonedDay,
                timeZone
              ).toISOString();

              handleDay(eventDay);
              handleTime(eventStart);
              setDateAndTime(
                attachHoursToDate(normalizedDayIso, eventStart, timeZone)
              );

              return history.push(
                `${RESERVATIONS_MAP_ROOT_PATH}/${clickedMarker[0].id}/markerOpen=${workplaceId}`
              );
            }

            setActiveModal(value as ModalTypes);

            setShowDropdown(false);

            toggleModal();
          }}
        />
      )}
      {isOpen && <Component {...componentProps}></Component>}
    </div>
  );
};
